export interface FpsMonitorOptions {
  windowMs: number; // sliding window size for FPS computation
  thresholdFps: number; // warning threshold
  sustainedMs: number; // how long FPS must stay below threshold to warn
  cooldownMs: number; // silence subsequent warnings for this long after one fires
}

export interface FpsMonitor {
  tick(now: number): void;
  getCurrentFps(): number;
  onWarning(cb: () => void): () => void;
}

const DEFAULTS: FpsMonitorOptions = {
  windowMs: 5000,
  thresholdFps: 25,
  sustainedMs: 5000,
  cooldownMs: 30_000,
};

export function createFpsMonitor(opts: Partial<FpsMonitorOptions> = {}): FpsMonitor {
  const config = { ...DEFAULTS, ...opts };
  const samples: number[] = [];
  let lowSinceMs: number | null = null;
  let lastWarnAt = 0;
  // Once a warning fires in an episode, stay quiet until FPS recovers above
  // threshold (which resets warnedThisEpisode and lowSinceMs together). The
  // cooldown only governs how soon a NEW episode can warn after the previous
  // recovered.
  let warnedThisEpisode = false;
  const listeners = new Set<() => void>();

  const computeFps = (now: number): number => {
    if (samples.length < 2) return 0;
    const oldest = samples[0] ?? now;
    const span = now - oldest;
    if (span <= 0) return 0;
    return ((samples.length - 1) * 1000) / span;
  };

  return {
    tick(now: number): void {
      samples.push(now);
      const cutoff = now - config.windowMs;
      while (samples.length > 0 && (samples[0] ?? 0) < cutoff) {
        samples.shift();
      }
      const fps = computeFps(now);
      if (fps > 0 && fps < config.thresholdFps) {
        if (lowSinceMs === null) lowSinceMs = now;
        const sustained = now - lowSinceMs;
        const cooldownOver = lastWarnAt === 0 || now - lastWarnAt >= config.cooldownMs;
        if (sustained >= config.sustainedMs && cooldownOver && !warnedThisEpisode) {
          lastWarnAt = now;
          warnedThisEpisode = true;
          for (const listener of listeners) {
            try {
              listener();
            } catch (err) {
              console.error('[fps-monitor] warning listener threw:', err);
            }
          }
        }
      } else {
        lowSinceMs = null;
        warnedThisEpisode = false;
      }
    },
    getCurrentFps(): number {
      const now = samples[samples.length - 1] ?? performance.now();
      return computeFps(now);
    },
    onWarning(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}
